import React, { useEffect, useState } from 'react';
import {
    Card,
    Stack,
    Typography,
    Avatar,
    Chip,
    LinearProgress,
    Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Divider,
} from '@mui/material';
import { differenceInYears, format } from 'date-fns';
import qrcode from 'qrcode-generator';
import { useLocales } from '../../locales';
import Iconify from '../../components/minimal/iconify';
import { LiveClock } from '../../components/minimal/LiveClock';

const QrCodeDialog = ({ open, onClose, sellerId, memberId, memberName, subscriptionName, idOrPassport }) => {
    const { translate } = useLocales();
    const [qrSvg, setQrSvg] = useState('');

    useEffect(() => {
        if (open && sellerId && memberId) {
            try {
                const qr = qrcode(6, 'Q');
                qr.addData(`sub/${sellerId}/${memberId}`);
                qr.make();
                setQrSvg(qr.createSvgTag(5));
            } catch (e) {
                console.error('QR code generation failed:', e);
            }
        } else {
            setQrSvg('');
        }
    }, [open, sellerId, memberId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
                <Typography variant="h6" fontWeight="bold">{translate('dashboard.profile_settings.entrance_code_modal_title')}</Typography>
                <IconButton onClick={onClose} size="small" aria-label={translate('close')}>
                    <Iconify width="20px" icon="eva:close-fill" />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack alignItems="center" spacing={2.5} sx={{ pt: 2, pb: 1 }}>
                    {/* Subscription name chip */}
                    {subscriptionName && (
                        <Chip
                            label={subscriptionName}
                            color="primary"
                            variant="soft"
                            size="medium"
                            sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                        />
                    )}

                    {/* QR Code */}
                    <Box
                        dangerouslySetInnerHTML={{ __html: qrSvg }}
                        sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: 'background.neutral',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: (theme) => theme.customShadows?.z8 || '0 8px 16px 0 rgba(0,0,0,0.08)',
                            '& svg': { width: '100%', maxWidth: 220, height: 'auto', display: 'block' },
                        }}
                    />

                    {/* Member info + clock */}
                    <Stack spacing={0.5} alignItems="center">
                        {memberName && (
                            <Typography variant="h6" fontWeight="bold" textAlign="center">
                                {memberName}
                            </Typography>
                        )}
                        {idOrPassport && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                    {translate('family.id')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    {idOrPassport}
                                </Typography>
                            </Stack>
                        )}
                        <LiveClock />
                    </Stack>
                </Stack>
            </DialogContent>

            {/* Footer — outside DialogContent so divider spans edge to edge */}
            <Divider />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ px: 3, py: 1.5, color: 'text.disabled' }}>
                <Iconify icon="eva:smartphone-outline" width={16} />
                <Typography variant="caption">
                    {translate('family.scan_to_check_in')}
                </Typography>
            </Stack>
        </Dialog>
    );
};

const FamilyMemberCard = ({ member, endUser, isSelf, onComplete, sellerId, subscriptionName }) => {
    const { translate } = useLocales();
    const [qrOpen, setQrOpen] = useState(false);

    const isComplete = member?.isProfileComplete;
    const isActive = member?.status === 'active';
    // The slot can be paid/active while the member's profile is still incomplete — and until it
    // is, they can't actually be used (no check-in QR). So only present them as "active" once
    // both hold; otherwise the badge contradicts the incomplete border / complete button / banner.
    const isFullyActive = isActive && isComplete;
    const canShowQr = isFullyActive && Boolean(sellerId) && Boolean(member?._id);

    // Use endUser data if available, fall back to member-level data
    const fullName =
        endUser?.fname || endUser?.lname
            ? `${endUser?.fname || ''} ${endUser?.lname || ''}`.trim()
            : member?.memberName || null;

    const birthDate = endUser?.birth_date || member?.birth_date;
    const phone = endUser?.phone || member?.phone;
    const age = birthDate
        ? differenceInYears(new Date(), new Date(birthDate))
        : null;

    const isChild = member?.familyRole === 'child';

    const typeLabel = isChild
        ? age !== null
            ? translate('family.child_age', { age })
            : translate('family.child')
        : translate('family.adult');

    const usedTransactions = member?.eventTransactionUsed ?? 0;
    const usageProgress =
        member?.eventTransactionAllowed > 0
            ? (usedTransactions / member.eventTransactionAllowed) * 100
            : 0;

    const showProgress = member?.eventTransactionAllowed > 0;

    const cardSx = isComplete
        ? {}
        : {
              border: '2px dashed',
              borderColor: 'warning.main',
          };

    const nameForInitials = endUser?.fname || endUser?.lname
        ? `${(endUser?.fname || '')[0] || ''}${(endUser?.lname || '')[0] || ''}`
        : member?.memberName
            ? member.memberName.split(' ').map(w => w[0] || '').join('')
            : '';
    const initials = nameForInitials ? nameForInitials.toUpperCase() : '?';

    return (
        <Card sx={{ p: 2.5, ...cardSx }}>
            <Stack spacing={2}>
                {/* Top row: avatar + info + badges */}
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                        <Avatar
                            src={endUser?.imageUrl || member?.imageUrl || undefined}
                            sx={{ width: 56, height: 56, fontSize: 20 }}
                        >
                            {!(endUser?.imageUrl || member?.imageUrl) && initials}
                        </Avatar>
                        {isSelf && (
                            <Chip
                                label={translate('family.you')}
                                size="small"
                                color="primary"
                                sx={{
                                    position: 'absolute',
                                    bottom: -8,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    height: 16,
                                    fontSize: 9,
                                    fontWeight: 700,
                                }}
                            />
                        )}
                    </Box>

                    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle1" fontWeight={600} noWrap>
                                {fullName || translate('family.details_required')}
                            </Typography>
                            <Chip
                                label={isFullyActive ? translate('active') : translate('not_active')}
                                size="small"
                                color={isFullyActive ? 'success' : 'warning'}
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11 }}
                            />
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                            {typeLabel}
                        </Typography>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Iconify icon="eva:phone-fill" width={14} height={14} color="text.disabled" />
                            <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr' }}>
                                {phone ? <bdi>{phone}</bdi> : translate('family.no_phone')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>

                {/* Usage progress */}
                {showProgress && (
                    <Box>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('pricing.used_entries')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {usedTransactions} / {member.eventTransactionAllowed}
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(usageProgress, 100)}
                            color={usageProgress >= 100 ? 'error' : 'primary'}
                            sx={{ height: 6, borderRadius: 3 }}
                        />
                    </Box>
                )}

                {/* Action buttons */}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {!isSelf && !isComplete && (
                        <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={() => onComplete && onComplete(member)}
                            startIcon={<Iconify icon="eva:edit-2-fill" />}
                        >
                            {translate('family.complete_action')}
                        </Button>
                    )}


                    {canShowQr && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setQrOpen(true)}
                            startIcon={<Iconify icon="eva:maximize-outline" />}
                        >
                            {translate('family.show_qr')}
                        </Button>
                    )}
                </Stack>
            </Stack>

            <QrCodeDialog
                open={qrOpen && canShowQr}
                onClose={() => setQrOpen(false)}
                sellerId={sellerId}
                memberId={member?._id}
                memberName={fullName || ''}
                subscriptionName={subscriptionName}
                idOrPassport={endUser?.idOrPassport || member?.idOrPassport}
            />
        </Card>
    );
};

export default FamilyMemberCard;
